/**
 * BugReportModal.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect } from 'react';
import consoleCapture from '../utils/consoleCapture';
import { useToast } from './ToastContainer';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { fmt } from '../i18n/format';
import './BugReportModal.css';

const BugReportModal = ({ isOpen, onClose, pageContext }) => {
  const { showSuccess, showError, showInfo } = useToast();
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const ui = t.ui.bug;
  
  const [formData, setFormData] = useState({
    issueTitle: '',
    issueDescription: '',
    issueType: 'bug',
    userEmail: currentUser?.email || '',
    currentUrl: window.location.href,
    timestamp: new Date().toISOString(),
  });
  const [submitAnonymously, setSubmitAnonymously] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Auto-fill email if user logs in after initial render (only when not anonymous)
  useEffect(() => {
    if (isOpen && !submitAnonymously && currentUser?.email && !formData.userEmail) {
      setFormData(prev => ({ ...prev, userEmail: currentUser.email }));
    }
  }, [isOpen, currentUser, formData.userEmail, submitAnonymously]);

  const [browserInfo, setBrowserInfo] = useState({});
  const [consoleLogs, setConsoleLogs] = useState([]);
  const [logStats, setLogStats] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load captured console logs from global console capture
  useEffect(() => {
    if (!isOpen) return;

    // Get logs from the global console capture utility
    const logs = consoleCapture.getLogs();
    const stats = consoleCapture.getLogStats();
    
    setConsoleLogs(logs);
    setLogStats(stats);
    
    console.log('[BugReportModal] Loaded', logs.length, 'console logs from capture');
  }, [isOpen]);

  // Capture browser and device information
  useEffect(() => {
    if (!isOpen) return;

    const info = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      colorDepth: window.screen.colorDepth,
      pixelRatio: window.devicePixelRatio,
      cookiesEnabled: navigator.cookieEnabled,
      onlineStatus: navigator.onLine,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      localTime: new Date().toLocaleString(),
    };

    // Detect browser
    let browserName = 'Unknown';
    if (navigator.userAgent.indexOf('Chrome') > -1 && navigator.userAgent.indexOf('Edg') === -1) {
      browserName = 'Chrome';
    } else if (navigator.userAgent.indexOf('Safari') > -1 && navigator.userAgent.indexOf('Chrome') === -1) {
      browserName = 'Safari';
    } else if (navigator.userAgent.indexOf('Firefox') > -1) {
      browserName = 'Firefox';
    } else if (navigator.userAgent.indexOf('Edg') > -1) {
      browserName = 'Edge';
    } else if (navigator.userAgent.indexOf('MSIE') > -1 || navigator.userAgent.indexOf('Trident') > -1) {
      browserName = 'Internet Explorer';
    }
    info.browser = browserName;

    // Detect device type
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    info.deviceType = isMobile ? 'Mobile' : 'Desktop';

    setBrowserInfo(info);
  }, [isOpen]);

  const validateEmailFormat = (value) => {
    const trimmed = String(value || '').trim();
    if (!trimmed) {
      return ui.errEmailRequired;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      return ui.errEmailInvalid;
    }
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'userEmail' && !submitAnonymously) {
      setEmailError(value.trim() ? validateEmailFormat(value) : '');
    }
  };

  const handleAnonymousChange = (e) => {
    const checked = e.target.checked;
    setSubmitAnonymously(checked);
    setEmailError('');
    if (checked) {
      setFormData(prev => ({ ...prev, userEmail: '' }));
    } else if (currentUser?.email) {
      setFormData(prev => ({ ...prev, userEmail: currentUser.email }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!submitAnonymously) {
      const err = validateEmailFormat(formData.userEmail);
      if (err) {
        setEmailError(err);
        showError(err, 5000);
        return;
      }
    }

    setIsSubmitting(true);
    
    // Show info toast that we're submitting
    showInfo(ui.toastSubmitting, 2000);

    try {
      // Prepare console logs for submission (last 30 logs)
      const recentLogs = consoleLogs.slice(-30).map(log => 
        `[${log.timeFormatted || new Date(log.timestamp).toLocaleTimeString()}] [${log.type.toUpperCase()}] ${log.message}`
      ).join('\n');

      // Prepare the bug report with all data
      const bugReportData = {
        issueType: formData.issueType,
        issueTitle: formData.issueTitle,
        issueDescription: formData.issueDescription,
        userEmail: submitAnonymously ? '' : formData.userEmail.trim(),
        submitAnonymously,
        currentUrl: formData.currentUrl,
        timestamp: formData.timestamp,
        browserInfo: browserInfo,
        consoleLogs: recentLogs || 'No console logs captured',
        pageContext: pageContext || null,
      };

      // Send to backend API
      const response = await fetch('/api/bug-report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bugReport: bugReportData
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Show success toast
        showSuccess(ui.toastSuccess, 5000);
        
        // Close modal and reset form
        setTimeout(() => {
          onClose();
          setSubmitAnonymously(false);
          setEmailError('');
          setFormData({
            issueTitle: '',
            issueDescription: '',
            issueType: 'bug',
            userEmail: currentUser?.email || '',
            currentUrl: window.location.href,
            timestamp: new Date().toISOString(),
          });
        }, 500);
      } else {
        throw new Error(result.error || 'Failed to submit bug report');
      }
    } catch (error) {
      console.error('Error submitting bug report:', error);
      
      showError(error.message || ui.toastFailure, 7000);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="bug-report-overlay" onClick={onClose}>
      <div className="bug-report-modal" onClick={(e) => e.stopPropagation()}>
        <h2>
          <span className="bug-icon">🐛</span>
          {ui.title}
        </h2>
        <p>{ui.intro}</p>

        <form onSubmit={handleSubmit} className="bug-report-form">
          <div className="bug-form-group">
            <label htmlFor="issueType">
              {ui.issueType}<span className="required">*</span>
            </label>
            <select
              id="issueType"
              name="issueType"
              value={formData.issueType}
              onChange={handleChange}
              required
            >
              <option value="bug">{ui.typeBug}</option>
              <option value="ui">{ui.typeUi}</option>
              <option value="performance">{ui.typePerformance}</option>
              <option value="feature">{ui.typeFeature}</option>
              <option value="other">{ui.typeOther}</option>
            </select>
          </div>

          <div className="bug-form-group">
            <label htmlFor="issueTitle">
              {ui.issueTitle}<span className="required">*</span>
            </label>
            <input
              type="text"
              id="issueTitle"
              name="issueTitle"
              value={formData.issueTitle}
              onChange={handleChange}
              placeholder={ui.issueTitlePlaceholder}
              required
              maxLength={100}
            />
          </div>

          <div className="bug-form-group">
            <label htmlFor="issueDescription">
              {ui.description}<span className="required">*</span>
            </label>
            <textarea
              id="issueDescription"
              name="issueDescription"
              value={formData.issueDescription}
              onChange={handleChange}
              placeholder={ui.descriptionPlaceholder}
              required
              minLength={20}
            />
          </div>

          <div className="bug-form-group bug-form-group-checkbox">
            <label className="bug-anonymous-label" htmlFor="submitAnonymously">
              <input
                type="checkbox"
                id="submitAnonymously"
                name="submitAnonymously"
                checked={submitAnonymously}
                onChange={handleAnonymousChange}
              />
              <span>{ui.anonymous}</span>
            </label>
            <p className="bug-anonymous-note">
              {submitAnonymously ? ui.anonymousNoteOn : ui.anonymousNoteOff}
            </p>
          </div>

          {!submitAnonymously && (
            <div className="bug-form-group">
              <label htmlFor="userEmail">
                {ui.yourEmail}<span className="required">*</span>
              </label>
              <input
                type="email"
                id="userEmail"
                name="userEmail"
                value={formData.userEmail}
                onChange={handleChange}
                onBlur={() => setEmailError(validateEmailFormat(formData.userEmail))}
                placeholder={ui.emailPlaceholder}
                required
                autoComplete="email"
                className={emailError ? 'input-error' : ''}
              />
              {emailError && <span className="bug-field-error">{emailError}</span>}
            </div>
          )}

          <div className="bug-form-group">
            <label htmlFor="currentUrl">{ui.currentUrl}</label>
            <input
              type="text"
              id="currentUrl"
              name="currentUrl"
              value={formData.currentUrl}
              disabled
            />
          </div>

          {pageContext && pageContext.openPopups && pageContext.openPopups.length > 0 && (
            <div className="bug-context-info">
              <strong>🪟 {ui.openPopups}</strong>
              <ul className="bug-context-popups">
                {pageContext.openPopups.map((label, i) => (
                  <li key={i}>{label}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="bug-diagnostic-info">
            <strong>📊 {ui.diagnostics}</strong>
            <div className="bug-diagnostic-grid">
              <span className="bug-diagnostic-label">{ui.browser}</span>
              <span className="bug-diagnostic-value">{browserInfo.browser || ui.loadingValue}</span>

              <span className="bug-diagnostic-label">{ui.device}</span>
              <span className="bug-diagnostic-value">{browserInfo.deviceType || ui.loadingValue}</span>

              <span className="bug-diagnostic-label">{ui.screen}</span>
              <span className="bug-diagnostic-value">{browserInfo.screenResolution || ui.loadingValue}</span>

              <span className="bug-diagnostic-label">{ui.viewport}</span>
              <span className="bug-diagnostic-value">{browserInfo.viewportSize || ui.loadingValue}</span>
            </div>
          </div>

          <div className="bug-console-info">
            <strong>
              📝 {fmt(ui.consoleCaptured, { count: consoleLogs.length })}
              {logStats.total > 0 && (
                <span style={{ marginLeft: '10px', fontSize: '12px', color: '#666' }}>
                  (
                  {logStats.error > 0 && (
                    <span style={{ color: '#e74c3c' }}>
                      ❌ {fmt(ui.errorsCount, { count: logStats.error })}
                    </span>
                  )}
                  {logStats.error > 0 && (logStats.warn > 0 || logStats.info > 0 || logStats.log > 0) && ', '}
                  {logStats.warn > 0 && (
                    <span style={{ color: '#f39c12' }}>
                      ⚠️  {fmt(ui.warningsCount, { count: logStats.warn })}
                    </span>
                  )}
                  {logStats.warn > 0 && (logStats.info > 0 || logStats.log > 0) && ', '}
                  {logStats.info > 0 && <span>{fmt(ui.infoCount, { count: logStats.info })}</span>}
                  {logStats.info > 0 && logStats.log > 0 && ', '}
                  {logStats.log > 0 && <span>{fmt(ui.logsCount, { count: logStats.log })}</span>}
                  )
                </span>
              )}
            </strong>
            <div className="bug-console-preview">
              {consoleLogs.length > 0 ? (
                consoleLogs.slice(-5).map((log, idx) => (
                  <div key={idx} style={{ 
                    color: log.type === 'error' ? '#ff6b6b' : 
                           log.type === 'warn' ? '#ffd93d' : 
                           log.type === 'info' ? '#6bcfff' : '#f8f8f2'
                  }}>
                    [{log.timeFormatted || new Date(log.timestamp).toLocaleTimeString()}] [{log.type.toUpperCase()}] {log.message.substring(0, 100)}
                    {log.message.length > 100 ? '...' : ''}
                  </div>
                ))
              ) : (
                <div className="bug-console-empty">{ui.noLogs}</div>
              )}
            </div>
          </div>

          <div className="bug-form-actions">
            <button
              type="button"
              className="bug-btn bug-btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t.ui.common.cancel}
            </button>
            <button
              type="submit"
              className="bug-btn bug-btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="bug-submit-icon">⏳</span>
                  {ui.submitting}
                </>
              ) : (
                <>
                  <span className="bug-submit-icon">📮</span>
                  {ui.submit}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BugReportModal;

